import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { jobsAPI } from '../services/api';
import { Job } from '../types';
import { Card, Button, EmptyState, SectionHeader, Pill } from '../components/ui';
import { Icon } from '../components/ui/Icon';
import { SEO } from '../components/seo/SEO';
import { jobUrl, slugify } from '../utils/slug';
import { formatJobExperience, hasExperienceRange } from '../utils/experienceRange';
import { fadeInUp, stagger } from '../utils/motion';

/**
 * Programmatic SEO landing page for "Jobs in <City>".
 * Filters the global jobs list by location at fetch time. For SEO, bots get the
 * Flask SSR version which has the rendered HTML + JobPosting ItemList JSON-LD.
 * Humans hit this React page and get the same data via the API.
 */

const formatCityFromSlug = (slug: string): string => {
  if (slug === 'remote') return 'Remote';
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const LocationLanding: React.FC = () => {
  const { city: citySlugParam } = useParams<{ city: string }>();
  const citySlug = (citySlugParam || '').toLowerCase();
  const cityDisplay = formatCityFromSlug(citySlug);
  const isRemote = citySlug === 'remote';

  const [jobs, setJobs] = React.useState<Job[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Pull a wider set and client-filter by slugified location.
        // We can't push slug-equality to the API without backend support, so we
        // approximate with the location keyword filter and then refine here.
        const params: any = { per_page: 50 };
        if (isRemote) {
          params.remote_allowed = true;
        } else {
          params.location = cityDisplay;
        }
        const resp = await jobsAPI.getJobs(params);
        if (cancelled) return;
        const allMatching = (resp.jobs || []).filter((j) =>
          isRemote ? j.remote_allowed : j.location && slugify(j.location) === citySlug
        );
        setJobs(allMatching);
        setTotal(allMatching.length);
      } catch (e) {
        console.error('Failed to load location jobs:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [citySlug, cityDisplay, isRemote]);

  const canonicalUrl = `${process.env.REACT_APP_PUBLIC_BASE_URL || 'https://talixjobs.com'}/jobs/in/${citySlug}`;

  const itemListJsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    name: `Jobs in ${cityDisplay}`,
    numberOfItems: total,
    itemListElement: jobs.slice(0, 20).map((j, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${process.env.REACT_APP_PUBLIC_BASE_URL || 'https://talixjobs.com'}${jobUrl(j)}`,
      name: j.title,
    })),
  };

  return (
    <div className="container portal-v3-page">
      <SEO
        title={`Jobs in ${cityDisplay}`}
        description={`${total || 'Open'} role${total === 1 ? '' : 's'} in ${cityDisplay}. AI-ranked roles from top companies on talixjobs.`}
        canonicalUrl={canonicalUrl}
        jsonLd={total > 0 ? itemListJsonLd : undefined}
      />

      <SectionHeader
        eyebrow="Location"
        title={`Jobs in ${cityDisplay}`}
        description={loading ? 'Loading roles…' : `${total} open role${total === 1 ? '' : 's'} matching your search.`}
      />

      {loading ? (
        <Card padded>
          <div style={{ textAlign: 'center', padding: 'var(--s-6)', color: '#64748b' }}>
            Loading jobs in {cityDisplay}…
          </div>
        </Card>
      ) : jobs.length === 0 ? (
        <EmptyState
          title={`No active jobs in ${cityDisplay} right now`}
          description="Try browsing all roles or check back soon — new positions are added every day."
          icon={<Icon name="search" size={28} />}
          action={
            <Link to="/jobs" className="ui-btn ui-btn--primary ui-btn--sm">
              <span className="ui-btn__label">Browse all jobs</span>
            </Link>
          }
        />
      ) : (
        <motion.div variants={stagger(0.04)} initial="hidden" animate="visible" className="location-landing__list">
          {jobs.map((job) => (
            <motion.div key={job.id} variants={fadeInUp}>
              <Card padded className="location-landing__row">
                <div className="location-landing__row-main">
                  <Link to={jobUrl(job)} className="location-landing__title">{job.title}</Link>
                  <div className="location-landing__sub">
                    {job.company?.name && <span>{job.company.name}</span>}
                    {job.location && (<><span>•</span><span>{job.location}</span></>)}
                    {job.job_type && (<><span>•</span><span>{job.job_type}</span></>)}
                    {hasExperienceRange(job) && (<><span>•</span><span>{formatJobExperience(job)}</span></>)}
                    {(job.salary_min || job.salary_max) && (
                      <>
                        <span>•</span>
                        <span>
                          {job.salary_currency || 'INR'}{' '}
                          {job.salary_min ?? ''}
                          {job.salary_min && job.salary_max ? '–' : ''}
                          {job.salary_max ?? ''}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="location-landing__skills">
                    {(job.skills || []).slice(0, 4).map((s) => (
                      <Pill key={s.id} tone="neutral">{s.skill?.name}</Pill>
                    ))}
                    {job.remote_allowed && <Pill tone="success">Remote OK</Pill>}
                  </div>
                </div>
                <div className="location-landing__row-side">
                  <Button
                    variant="primary"
                    size="sm"
                    iconRight={<Icon name="chevron-right" size={14} />}
                    onClick={() => (window.location.href = jobUrl(job))}
                  >
                    View
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}

      <Card padded>
        <SectionHeader eyebrow="More to explore" title="Related searches" />
        <div className="location-landing__related">
          <Link to="/jobs" className="ui-btn ui-btn--secondary ui-btn--sm">
            <span className="ui-btn__label">All jobs across India</span>
          </Link>
          {!isRemote && (
            <Link to="/jobs/in/remote" className="ui-btn ui-btn--secondary ui-btn--sm">
              <span className="ui-btn__label">Remote-friendly roles</span>
            </Link>
          )}
          <Link to="/" className="ui-btn ui-btn--ghost ui-btn--sm">
            <span className="ui-btn__label">talixjobs home</span>
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default LocationLanding;
